import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react";
import { useFormik } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DateTimePicker from "../Inputs/DateTimePicker";
import {
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Alert,
  Button
} from "@mui/material";
import { useRecoilValue } from "recoil";
import { streamState, authState } from "../../Recoil/RecoilState";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const makeInitial = (filter) => ({
  camera_id: Array.isArray(filter.camera_id)
    ? filter.camera_id
    : filter.camera_id
    ? filter.camera_id.split(",")
    : [],
  startDate: filter.startDate ? dayjs(filter.startDate, "DD/MM/YYYY") : null,
  endDate: filter.endDate ? dayjs(filter.endDate, "DD/MM/YYYY") : null,
  startTime: filter.startTime ? dayjs(filter.startTime, "HH:mm") : null,
  endTime: filter.endTime ? dayjs(filter.endTime, "HH:mm") : null,
  buildings: Array.isArray(filter.buildings) ? filter.buildings : filter.buildings?.split(",") || [],
  floors: Array.isArray(filter.floors) ? filter.floors : filter.floors?.split(",") || [],
  zones: Array.isArray(filter.zones) ? filter.zones : filter.zones?.split(",") || [],
  areas: Array.isArray(filter.areas) ? filter.areas : filter.areas?.split(",") || [],
  locations: Array.isArray(filter.locations) ? filter.locations : filter.locations?.split(",") || [],
});

const PredictionFilter = forwardRef(({ filter, changeFilterHandle }, ref) => {
  const { data: cameraOptions, loading: cameraLoading, error: cameraError } = useRecoilValue(streamState);
  const { token } = useRecoilValue(authState);

  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [zones, setZones] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hierarchyError, setHierarchyError] = useState(null);

  const formik = useFormik({
    initialValues: makeInitial(filter),
    enableReinitialize: true,
    onSubmit: (values) => {
      changeFilterHandle({
        camera_id: values.camera_id.join(","),
        startDate: values.startDate?.format("DD/MM/YYYY") ?? null,
        endDate: values.endDate?.format("DD/MM/YYYY") ?? null,
        startTime: values.startTime?.format("HH:mm") ?? null,
        endTime: values.endTime?.format("HH:mm") ?? null,
        buildings: values.buildings.join(","),
        floors: values.floors.join(","),
        zones: values.zones.join(","),
        areas: values.areas.join(","),
        locations: values.locations.join(","),
      });
    },
  });

  const clearAllFilters = () => {
    formik.resetForm({
      values: {
        locations: [],
        areas: [],
        buildings: [],
        floors: [],
        zones: [],
        camera_id: [], // Fixed: was camera_ids, should be camera_id
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
      }
    });
    
    // Clear dependent state arrays
    setAreas([]);
    setBuildings([]);
    setFloors([]);
    setZones([]);
    setCameras([]);
  };

  // 🟢 Fetch Locations
  useEffect(() => {
    const fetchLocations = async () => {
      setLoading(true);
      setHierarchyError(null);
      try {
        const { data } = await axios.get(`${baseURL}locations/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLocations(data.locations || []);
      } catch (error) {
        setHierarchyError("Failed to load locations");
        console.error("Error fetching locations:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchLocations();
    }
  }, [token]);

  // 🟢 Fetch Areas when locations change
  useEffect(() => {
    if (formik.values.locations.length > 0) {
      const fetchAreas = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}areas/list?locations=${formik.values.locations.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setAreas(data.areas || []);
        } catch (error) {
          setHierarchyError("Failed to load areas");
          console.error("Error fetching areas:", error);
        }
      };
      fetchAreas();
    } else {
      setAreas([]);
      // Clear dependent selections
      formik.setFieldValue("areas", []);
      setBuildings([]);
      formik.setFieldValue("buildings", []);
      setFloors([]);
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.locations, token]);

  // 🟢 Fetch Buildings when areas change
  useEffect(() => {
    if (formik.values.areas.length > 0) {
      const fetchBuildings = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}buildings/list?areas=${formik.values.areas.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBuildings(data.buildings || []);
        } catch (error) {
          setHierarchyError("Failed to load buildings");
          console.error("Error fetching buildings:", error);
        }
      };
      fetchBuildings();
    } else {
      setBuildings([]);
      // Clear dependent selections
      formik.setFieldValue("buildings", []);
      setFloors([]);
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.areas, token]);

  // 🟢 Fetch Floors when buildings change
  useEffect(() => {
    if (formik.values.buildings.length > 0) {
      const fetchFloors = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}floor-levels/list?buildings=${formik.values.buildings.join(",")}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setFloors(data.floor_levels || []);
        } catch (error) {
          setHierarchyError("Failed to load floors");
          console.error("Error fetching floors:", error);
        }
      };
      fetchFloors();
    } else {
      setFloors([]);
      // Clear dependent selections
      formik.setFieldValue("floors", []);
      setZones([]);
      formik.setFieldValue("zones", []);
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.buildings, token]);

  // 🟢 Fetch Zones when floors change
  useEffect(() => {
    if (formik.values.floors.length > 0) {
      const fetchZones = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}zones/list?floor_levels=${formik.values.floors.join(",")}`, // Fixed: was floor-levels_id
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setZones(data.zones || []);
        } catch (error) {
          setHierarchyError("Failed to load zones");
          console.error("Error fetching zones:", error);
        }
      };
      fetchZones();
    } else {
      setZones([]);
      // Clear dependent selections
      formik.setFieldValue("zones", []);
      setCameras([]);
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.floors, token]);

  // 🟢 Fetch Cameras when zones change
  useEffect(() => {
    if (formik.values.zones.length > 0) {
      const fetchCameras = async () => {
        setHierarchyError(null);
        try {
          const { data } = await axios.get(
            `${baseURL}streams/locations/search?location_type=zones&q=${formik.values.zones.join(",")}`, // Fixed: removed line break and fixed parameter name
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCameras(data || []);
        } catch (error) {
          setHierarchyError("Failed to load cameras");
          console.error("Error fetching cameras:", error);
        }
      };
      fetchCameras();
    } else {
      setCameras([]);
      // Clear dependent selections
      formik.setFieldValue("camera_id", []);
    }
  }, [formik.values.zones, token]);

  useImperativeHandle(ref, () => ({
    submit: formik.handleSubmit,
  }));

  const handleCheckboxChange = (field, value) => {
    const selected = Array.isArray(formik.values[field]) ? formik.values[field] : [];
    const updated = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    formik.setFieldValue(field, updated);
  };

  const handleSelectAll = (field, options, valueKey) => {
    const allValues = options.map(opt => opt[valueKey]);
    const currentValues = formik.values[field] || [];
    const allSelected = allValues.every(val => currentValues.includes(val));
    
    formik.setFieldValue(field, allSelected ? [] : allValues);
  };

  const renderCheckboxGroup = (label, field, options, valueKey = "location", isLoading = false, displayKey = null) => {
    const selectedCount = formik.values[field]?.length || 0;
    const totalCount = options.length;
    const actualDisplayKey = displayKey || valueKey;
    
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography fontWeight={600}>
            {label} {selectedCount > 0 && `(${selectedCount} selected)`}
          </Typography>
          {options.length > 0 && (
            <Button
              size="small"
              onClick={() => handleSelectAll(field, options, valueKey)}
            >
              {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </Stack>
        
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : options.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={1}>
            No {label.toLowerCase()} available
          </Typography>
        ) : (
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 1,
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 1
          }}>
            {options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                control={
                  <Checkbox
                    size="small"
                    checked={formik.values[field]?.includes(opt[valueKey]) || false}
                    onChange={() => handleCheckboxChange(field, opt[valueKey])}
                  />
                }
                label={opt[actualDisplayKey]}
                sx={{ margin: 0 }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontWeight={600}>Location Filters</Typography>
        <Button size="small" onClick={clearAllFilters} color="secondary">
          Clear All Filters
        </Button>
      </Stack>
      
      <Stack gap={3} component="form" onSubmit={formik.handleSubmit}>
        {loading && <CircularProgress size={24} />}
        {hierarchyError && <Alert severity="error">{hierarchyError}</Alert>}
        
        {!loading && !hierarchyError && (
          <>
            {renderCheckboxGroup("Locations", "locations", locations)}
            {formik.values.locations.length > 0 && renderCheckboxGroup("Areas", "areas", areas, 'area')}
            {formik.values.areas.length > 0 && renderCheckboxGroup("Buildings", "buildings", buildings, 'building')}
            {formik.values.buildings.length > 0 && renderCheckboxGroup("Floors", "floors", floors, 'floor_level')}
            {formik.values.floors.length > 0 && renderCheckboxGroup("Zones", "zones", zones, "zone")}
            {formik.values.zones.length > 0 && renderCheckboxGroup("Cameras", "camera_id", cameras, "id", false, "name")}
          </>
        )}

        {/* Legacy Cameras Section (if needed for backward compatibility) */}
        {/* {cameraOptions?.length > 0 && (
          <Box>
            <Typography fontWeight={600} mb={1}>All Available Cameras:</Typography>
            {cameraLoading && <CircularProgress size={24} />}
            {cameraError && <Alert severity="error">{cameraError}</Alert>}
            {!cameraLoading && !cameraError && (
              <Box sx={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 1,
                maxHeight: 200,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 1
              }}>
                {cameraOptions.map((cam) => (
                  <FormControlLabel
                    key={cam.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={formik.values.camera_id.includes(cam.id)}
                        onChange={() => handleCheckboxChange("camera_id", cam.id)}
                      />
                    }
                    label={cam.name}
                    sx={{ margin: 0 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )} */}

        {/* Date and Time Pickers */}
        <DateTimePicker
          label="Start"
          dateValue={formik.values.startDate}
          timeValue={formik.values.startTime}
          onDateChange={(d) => formik.setFieldValue("startDate", d)}
          onTimeChange={(t) => formik.setFieldValue("startTime", t)}
          maxDate={formik.values.endDate}
        />
        <DateTimePicker
          label="End"
          dateValue={formik.values.endDate}
          timeValue={formik.values.endTime}
          onDateChange={(d) => formik.setFieldValue("endDate", d)}
          onTimeChange={(t) => formik.setFieldValue("endTime", t)}
          minDate={formik.values.startDate}
        />
      </Stack>
    </LocalizationProvider>
  );
});

PredictionFilter.displayName = 'PredictionFilter';

export default PredictionFilter;